import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Dropdown, Typography } from "antd";
import { isEmpty, startCase, toLower } from "lodash";
import { DownOutlined } from "@ant-design/icons";
import { ProductSelectorDropdown } from "./_components/SelectorDropdown.component";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useTopbarLayoutStore from "../Topbar/_store/TopbarLayout.store";
import { PRODUCTS_MAP } from "./_static";
const prepareProductName = (currentProduct) => { var _a, _b; return (_b = (_a = PRODUCTS_MAP[currentProduct]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : productNameFallback(currentProduct); };
/** Generate product name from id */
const productNameFallback = (currentProduct) => startCase(toLower(currentProduct.replace("TE_", "").replaceAll("_", " ")));
/**
 * A clickable dropdown lets users switch between TimeEdit products,
 * organized by category and then alphabetically.
 * It’s shown in the topbar for users with access to multiple products,
 * based on their permissions and the organization’s enabled products.
 * The current product is highlighted. Accessibility will be improved.
 */
export const ProductSelector = ({ currentProduct: selectedProduct, organization, user, appEnv }) => {
    var _a, _b;
    // use set current product if using inside the topbar
    const { currentProduct = selectedProduct } = useTopbarLayoutStore();
    const currentProductName = useMemo(() => {
        return (_jsx(Typography.Title, { level: 5, className: "!te-m-auto te-hidden sm:te-block", children: `${prepareProductName(currentProduct)}` }));
    }, [currentProduct]);
    const productSelectorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const iconFn = (_a = PRODUCTS_MAP[currentProduct]) === null || _a === void 0 ? void 0 : _a.icon;
    const productIcon = iconFn && (_jsx("div", { className: "te-flex te-items-center", children: iconFn(32) }));
    const otherProducts = (_b = organization.enabledProducts) === null || _b === void 0 ? void 0 : _b.filter((product) => product !== currentProduct);
    const handleKeyboard = useCallback((e) => {
        var _a;
        if (e.key === " " || e.key === "Enter") {
            setIsOpen(!isOpen);
            e.stopPropagation();
            e.preventDefault();
        }
        // if we press tab, and this is the last item, we close the popup.
        if (e.key === "Tab") {
            const focusElement = document.activeElement;
            if (!((_a = focusElement === null || focusElement === void 0 ? void 0 : focusElement.parentNode) === null || _a === void 0 ? void 0 : _a.parentNode))
                return;
            const isLastChild = focusElement.parentNode.parentNode.querySelector("li:last-child a") === e.target;
            if (isLastChild) {
                setIsOpen(false);
            }
        }
    }, [isOpen]);
    const handleOnClick = useCallback((e) => {
        if (isOpen)
            return;
        setIsOpen(true);
        e.stopPropagation();
    }, [isOpen]);
    // handle document click when menu is open
    const onDocumentClick = useCallback((e) => {
        const target = e.target;
        const { current: productSelector } = productSelectorRef;
        if (!productSelector)
            return;
        const dropdownItem = productSelector.querySelector(".te-product-selector-container");
        if (dropdownItem === target || dropdownItem.contains(target)) {
            return;
        }
        setIsOpen(false);
    }, []);
    useEffect(() => {
        if (!isOpen)
            return;
        document.addEventListener("click", onDocumentClick);
        return () => {
            document.removeEventListener("click", onDocumentClick);
        };
    }, [isOpen]);
    return (_jsx("div", { className: `
        product-selector te-flex te-h-full te-items-center te-rounded-md
        focus-visible:after:te-hidden
        after:te-block after:te-w-[.125rem] after:te-h-[1.5rem] after:te-bg-te-colorTextHeading
      `, "data-testid": "te-product-selector", tabIndex: 0, onClick: handleOnClick, onKeyDown: handleKeyboard, ref: productSelectorRef, children: isEmpty(otherProducts) ? (_jsxs("div", { className: "te-flex te-h-full te-items-center te-gap-2 te-pr-2 hover:te-cursor-pointer", "data-testid": "product-selector__trigger", children: [productIcon, currentProductName] })) : (_jsx(_Fragment, { children: _jsx(Dropdown, { open: isOpen, overlayClassName: "te-product-selector-container te-shadow-product-selector te-pt-8 te-pb-5 te-px-4 te-bg-white te-rounded-lg te-bg-white", arrow: true, getPopupContainer: (triggerNode) => triggerNode, placement: "bottomLeft", autoAdjustOverflow: false, popupRender: () => (_jsx(ProductSelectorDropdown, { appEnv: appEnv, organization: organization, user: user, currentProduct: currentProduct })), children: _jsxs("div", { lang: "x-no-translate", translate: "no", className: "te-flex te-items-center te-gap-2 te-pr-2 notranslate", "data-testid": "product-selector__trigger", children: [productIcon, currentProductName, _jsx(DownOutlined, {})] }) }) })) }));
};
